import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Crown, CheckCircle, CircleNotch, Leaf } from "@phosphor-icons/react";

const money = (cents) => `$${(cents / 100).toFixed(2)}`;

export default function SubscriptionPlans() {
  const { user, refreshUser } = useAuth();
  const access = user?.access || {};
  const [plans, setPlans] = useState([]);
  const [addon, setAddon] = useState(null);
  const [extraSeats, setExtraSeats] = useState(false);
  const [busy, setBusy] = useState("");
  const [promo, setPromo] = useState("");
  const [promoState, setPromoState] = useState(null); // {ok, message, code}
  const [promoBusy, setPromoBusy] = useState(false);

  const applyPromo = async () => {
    const code = promo.trim();
    if (!code) return;
    setPromoBusy(true);
    try {
      const { data } = await api.post("/subscriptions/promo/validate", { code });
      setPromoState({ ok: true, message: data.message, code: data.code });
    } catch (e) {
      setPromoState({ ok: false, message: formatApiErrorDetail(e.response?.data?.detail) || "That promo code isn't valid." });
    } finally { setPromoBusy(false); }
  };

  useEffect(() => {
    api.get("/subscriptions/plans")
      .then(({ data }) => { setPlans(data.plans); setAddon(data.seat_addon); })
      .catch(() => {});
  }, []);

  const hasActive = access.status === "active" && ["basic", "pro"].includes(access.tier);
  const currentAmount = (plans.find((p) => p.key === access.tier) || {}).amount || 0;
  const pending = user?.pending_downgrade;

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        plan, extra_seats: plan === "pro" && extraSeats, origin_url: window.location.origin,
        promo_code: plan === "pro" && promoState?.ok ? promoState.code : undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy("");
    }
  };

  const changePlan = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/subscriptions/change", { plan });
      toast.success(data.message || "Subscription updated");
      await refreshUser?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  return (
    <div>
      {pending?.to && (
        <div data-testid="pending-downgrade-banner"
          className="mb-4 rounded-lg border border-[#FFC857]/50 bg-[#FFF6E0] px-4 py-3 text-sm font-medium text-[#7A5A00]">
          Your plan will change to <span className="font-bold capitalize">{pending.to}</span> at the end of your current billing cycle.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="subscription-plans">
      {plans.map((p) => {
        const pro = p.key === "pro";
        const isCurrent = access.status === "active" && access.tier === p.key;
        const isChange = hasActive && !isCurrent;
        const isUpgrade = isChange && p.amount > currentAmount;
        const label = isCurrent ? "Current plan"
          : isChange ? (isUpgrade ? `Upgrade to ${p.name}` : `Downgrade to ${p.name}`)
          : `Choose ${p.name}`;
        return (
          <div key={p.key} data-testid={`plan-card-${p.key}`}
            className={`rounded-2xl border p-6 bg-white flex flex-col ${pro ? "border-[#3BB54A] ring-2 ring-[#3BB54A]" : "border-border"}`}>
            <div className="flex items-center gap-2">
              {pro ? <Crown size={22} weight="fill" className="text-[#2E2E2E]" /> : <Leaf size={22} weight="fill" className="text-[#2E2E2E]" />}
              <p className="font-display font-black text-xl text-[#2E2E2E]">{p.name}</p>
              {pro && <span className="text-[10px] font-bold uppercase tracking-wide bg-[#3BB54A] text-white px-2 py-0.5 rounded-full">Popular</span>}
            </div>
            <p className="font-display font-black text-3xl text-[#2E2E2E] mt-3">
              {money(p.amount)}<span className="text-base font-medium text-muted-foreground">/mo</span>
            </p>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground flex-1">
              {p.features.map((f, i) => (
                <li key={i} className="flex gap-2"><CheckCircle size={18} weight="fill" className="text-[#2E2E2E] shrink-0 mt-0.5" /> {f}</li>
              ))}
            </ul>
            {pro && addon && !isChange && (
              <label className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#2E2E2E] cursor-pointer" data-testid="extra-seats-toggle">
                <input type="checkbox" checked={extraSeats} onChange={(e) => setExtraSeats(e.target.checked)}
                  className="w-4 h-4 rounded accent-[#2E2E2E]" />
                Add 5 more seats (+{money(addon.amount)}/mo)
              </label>
            )}
            {pro && !isChange && !hasActive && (
              <div className="mt-4" data-testid="promo-code-section">
                <label className="block text-xs font-semibold text-[#2E2E2E] mb-1.5">Promo code</label>
                <div className="flex gap-2">
                  <input value={promo} onChange={(e) => { setPromo(e.target.value); setPromoState(null); }}
                    placeholder="Enter code" data-testid="promo-code-input"
                    className="flex-1 h-11 px-3 rounded-lg border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3BB54A]" />
                  <button type="button" onClick={applyPromo} disabled={promoBusy || !promo.trim()} data-testid="promo-apply-btn"
                    className="h-11 px-4 rounded-lg border-2 border-[#3BB54A] text-[#2E7D32] font-bold text-sm disabled:opacity-50 flex items-center gap-1.5">
                    {promoBusy && <CircleNotch size={16} className="animate-spin" />} Apply
                  </button>
                </div>
                {promoState && (
                  <p data-testid="promo-code-message" className={`mt-2 text-xs font-semibold ${promoState.ok ? "text-[#2E7D32]" : "text-red-600"}`}>
                    {promoState.message}
                  </p>
                )}
              </div>
            )}
            {isChange && (
              <p data-testid={`plan-change-note-${p.key}`} className={`mt-4 text-xs font-medium ${isUpgrade ? "text-[#2E7D32]" : "text-[#7A5A00]"}`}>
                {isUpgrade
                  ? "You will only be charged the prorated difference for this upgrade."
                  : "Your plan will change at the end of your current billing cycle."}
              </p>
            )}
            <button onClick={() => (isChange ? changePlan(p.key) : subscribe(p.key))} disabled={isCurrent || !!busy} data-testid={`subscribe-${p.key}`}
              className={`mt-5 h-12 rounded-full font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-60 ${pro ? "bg-[#2E2E2E] text-white hover:bg-[#2E9E3F] hover:text-white" : "border-2 border-[#3BB54A] text-[#2E2E2E] hover:bg-secondary"}`}>
              {busy === p.key && <CircleNotch size={18} className="animate-spin" />}
              {label}
            </button>
          </div>
        );
      })}
      </div>
    </div>
  );
}
